<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { isNavbarVisible } from '@/event/isNavbarVisible'
import { useRoute } from 'vue-router'
import { useCarousel } from '@/utils/main/home/useCarousel'
import { cartBus } from '@/event/cartBus'
import { sectionOrderBus } from '@/event/sectionOrderBus'
import {
    isOtherThemeHappy,
    isThemeDefault,
    isThemeHappy,
    isThemeIfoFamily,
    theme
} from '@/event/theme/theme'
import { computed, defineAsyncComponent, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { decodeJWT } from '@/utils/jwt/decodeJwt'
import { configAreas } from '@/storage/setting/configAreas'
import { alertTableQrCodeShow } from '@/event/alert/alertTableQrCodeShow'
import { nameTableRef } from '@/event/alert/nameTableRef'
import stores from '@/storage/stores'
import { formatStoreName } from '@/utils/store/formatStoreName'
import bannerHappy from '@/happyTheme/storage/bannerHappy'

const CatalogueCategory = defineAsyncComponent(
    () => import('@/components/main/CatalogCategory.vue')
)
const CatalogCategorySmoke = defineAsyncComponent(
    () => import('@/firstTheme/components/home/CatalogCategorySmoke.vue')
)

// Happy Theme
const CarouselBannerHappy = defineAsyncComponent(
    () => import('@/happyTheme/components/home/CarouselBannerHappy.vue')
)
const CatalogCustomHappy = defineAsyncComponent(
    () => import('@/happyTheme/components/home/CatalogCustomHappy.vue')
)

const route = useRoute()
const table = route.params.table

const handleTable = () => {
    if (table) {
        const decodeTable = decodeJWT(route.params.table)
        if (!decodeTable) {
            return
        }
        const [nameTable, numberTable] = decodeTable.table.split('-')
        const expirationTime = 30 * 60 * 1000
        const expirationTimestamp = new Date().getTime() + expirationTime
        if (
            Array.isArray(configAreas.value.areas) &&
            configAreas.value.length !== 0 &&
            configAreas.value.areas.length > 0
        ) {
            const area = configAreas.value.areas.find(
                (area: any) => area.areaName === nameTable
            )
            const store = stores.state.storeData.name
            const key = store ? `${formatStoreName(store)}_table` : 'table'

            if (area && numberTable <= area.maxTableNumber) {
                alertTableQrCodeShow.value = true
                nameTableRef.value = decodeTable.table

                localStorage.setItem(
                    key,
                    JSON.stringify({
                        value: decodeTable.table,
                        expiration: expirationTimestamp
                    })
                )

                setTimeout(() => {
                    alertTableQrCodeShow.value = false
                }, 3000)

                setTimeout(() => {
                    localStorage.removeItem(key)
                }, expirationTime)
            }
        }
    }
}

handleTable()

const {
    currentIndex,
    bannerData,
    carouselInner,
    touchStart,
    touchMove,
    touchEnd,
    mouseDown,
    mouseMove,
    mouseUp,
    updateCarouselPosition
} = useCarousel()

const toggleDisplay = () => {
    isNavbarVisible.value = true
    if (cartBus.value.length > 0) {
        sectionOrderBus.value = true
    }
}

const { t } = useI18n()

/**
 * Ifo family mobile UX: when the carousel has banners, it takes the full
 * above-the-fold slot and the category grid stays hidden behind a single
 * "Sipariş Ver" CTA pinned to the viewport bottom. Clicking the CTA reveals
 * the grid and hides the carousel. When the carousel has NO banners, we
 * skip this flow entirely — categories show immediately on mobile too.
 */
const showIfoCategoriesMobile = ref(false)

const hasIfoMobileHero = computed(
    () => isThemeIfoFamily.value && bannerHappy.state.banner.length > 0
)

function revealIfoCategories() {
    showIfoCategoriesMobile.value = true
    nextTick(() => {
        const el = document.querySelector('.section-div-custom-happy')
        if (el instanceof HTMLElement) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    })
}
</script>

<template>
    <div class="w-full overflow-hidden">
        <CatalogCategorySmoke v-if="theme === 'smoke' || theme === 'smoke-white'" />

        <!-- Happy -->
        <section
            class="w-full relative"
            :class="{
                'ifo-hero-mobile':
                    hasIfoMobileHero && !showIfoCategoriesMobile
            }"
            v-if="isThemeHappy || isOtherThemeHappy"
        >
            <CarouselBannerHappy
                :class="{
                    'ifo-carousel-hidden-mobile':
                        hasIfoMobileHero && showIfoCategoriesMobile
                }"
            />
            <CatalogCustomHappy
                :class="{
                    'ifo-categories-hidden-mobile':
                        hasIfoMobileHero && !showIfoCategoriesMobile
                }"
            />

            <!-- Ifo-only CTA: pinned bottom-center on mobile, reveals categories.
                 Only shown when there's a carousel to hero over — if the store
                 has no banners, categories surface directly on mobile too. -->
            <transition name="ifo-cta-fade">
                <button
                    v-if="hasIfoMobileHero && !showIfoCategoriesMobile"
                    type="button"
                    class="btn-reveal-ifo"
                    @click="revealIfoCategories()"
                >
                    <span class="btn-reveal-ifo-glow"></span>
                    <span class="btn-reveal-ifo-label">{{ t('orderCommand') }}</span>
                </button>
            </transition>
        </section>

        <!-- Default -->
        <div class="w-full" v-if="isThemeDefault">
            <section :class="{ section: isNavbarVisible, hidden: isNavbarVisible }">
                <div class="carousel" v-if="bannerData.length > 0">
                    <div class="carousel-inner" ref="carouselInner">
                        <div
                            v-for="(image, index) in bannerData"
                            :key="index"
                            class="carousel-item"
                            :class="{ active: currentIndex === index }"
                            @touchstart.passive="touchStart"
                            @touchmove.passive="touchMove"
                            @touchend.passive="touchEnd"
                            @mousedown.passive="mouseDown"
                            @mousemove.passive="mouseMove"
                            @mouseup.passive="mouseUp"
                        >
                            <img
                                :src="`${URL_IMAGE}${image.image}`"
                                :alt="image.title"
                                class="carousel-image"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div class="carousel-btn">
                        <button class="btn-order" @click="toggleDisplay">
                            Commander
                        </button>
                    </div>
                    <div class="carousel-dots">
                        <div
                            v-for="(image, index) in bannerData"
                            :key="image.title"
                            class="dot-container"
                        >
                            <div class="dot-white"></div>
                            <span
                                class="dot"
                                :class="{ active: currentIndex === index }"
                                @click="
                                    ((currentIndex = index), updateCarouselPosition())
                                "
                            ></span>
                        </div>
                    </div>
                </div>
            </section>
            <section class="section-mobile-first" :class="{ show: isNavbarVisible }">
                <CatalogueCategory class="catalog-category" />
            </section>
        </div>
    </div>
</template>

<style scoped>
/*  */

.catalog-category {
    @apply mx-2 w-full;
}

/* ──────────────────────────────────────────────────────────────────────
 * Ifo mobile CTA: hero carousel + floating "Sipariş Ver" button.
 * On screens < 640px, the category grid is hidden until the CTA is tapped.
 * After tap, the carousel hides and categories take over.
 *
 * The carousel now fills the entire viewport below the navbar like a
 * background image — user lands on a full-bleed hero, taps CTA to dive
 * into categories.
 * ─────────────────────────────────────────────────────────────────────── */
@media screen and (max-width: 639px) {
    :deep(.ifo-categories-hidden-mobile) {
        display: none !important;
    }
    :deep(.ifo-carousel-hidden-mobile) {
        display: none !important;
    }

    /* Hero wrapper locked to viewport height (minus navbar + optional
     * message banner) — no scroll, no footer leak. Fixed CTA floats above. */
    .ifo-hero-mobile {
        height: calc(100vh - var(--navbar-banner-total-height, 5.5rem));
        height: calc(100dvh - var(--navbar-banner-total-height, 5.5rem));
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background-color: rgb(var(--t-bg));
    }

    /* Flatten every carousel wrapper so the image can actually stretch.
     * Without these, the inner scoped CSS limits height: auto / min-h-full
     * and the image only takes its natural aspect-ratio height. Also zero
     * out the margin-top that normally pushes the carousel below the
     * message banner — our wrapper already accounts for it. */
    .ifo-hero-mobile :deep(.section-carousel-happy),
    .ifo-hero-mobile :deep(.section-carousel-message-special-happy) {
        flex: 1 1 auto;
        min-height: 0;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        display: flex;
        flex-direction: column;
    }
    .ifo-hero-mobile :deep(.carousel-happy) {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
    }
    .ifo-hero-mobile :deep(.carousel-inner-happy) {
        flex: 1 1 auto;
        min-height: 0;
        align-items: stretch;
    }
    .ifo-hero-mobile :deep(.carousel-item-happy) {
        min-height: 0;
        display: flex;
    }
    .ifo-hero-mobile :deep(.carousel-image-happy) {
        width: 100%;
        height: 100%;
        object-fit: cover;
        max-width: 100%;
    }

    /* Text overlay (if any) floats over the bottom of the image so the
     * photo is never cropped by a text block stacked below it. */
    .ifo-hero-mobile :deep(.carousel-details) {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 5.5rem; /* above the floating CTA */
        padding: 1rem 1.25rem;
        background: linear-gradient(
            to top,
            rgb(0 0 0 / 0.55),
            rgb(0 0 0 / 0.2) 70%,
            transparent
        );
        z-index: 5;
    }
    .ifo-hero-mobile :deep(.carousel-details *) {
        color: white !important;
        text-shadow: 0 1px 3px rgb(0 0 0 / 0.6);
    }

    /* Dots pulled well clear of the CTA so they don't look glued to the
     * button. CTA top-edge sits around 4.25rem from the viewport bottom
     * (1.75rem offset + ~2.5rem height), so 8rem gives a comfortable
     * ~3.75rem breathing gap. */
    .ifo-hero-mobile :deep(.carousel-dots) {
        bottom: 8rem !important;
    }
}

.btn-reveal-ifo {
    @apply fixed left-1/2 -translate-x-1/2 z-40;
    @apply px-10 py-3 rounded-full font-bold uppercase;
    @apply bg-t-card text-t-accent;
    @apply border border-t-accent;
    @apply transition-transform duration-200 ease-out;
    bottom: 1.75rem;
    letter-spacing: 0.18em;
    font-size: 0.85rem;
    overflow: hidden;
    animation: btn-reveal-ifo-pulse 2.4s ease-in-out infinite;
}

.btn-reveal-ifo:active {
    @apply scale-95;
    background-color: rgb(var(--t-accent) / 0.12);
}

/* Hide on tablet+ — categories are already visible there */
@media screen and (min-width: 640px) {
    .btn-reveal-ifo {
        display: none;
    }
}

.btn-reveal-ifo-label {
    @apply relative z-10;
    text-shadow: 0 0 8px rgb(var(--t-accent) / 0.35);
}

.btn-reveal-ifo-glow {
    @apply absolute inset-0 z-0 pointer-events-none;
    background: linear-gradient(
        120deg,
        transparent 0%,
        rgb(var(--t-accent) / 0.18) 45%,
        transparent 90%
    );
    background-size: 200% 100%;
    animation: btn-reveal-ifo-shimmer 3s linear infinite;
}

@keyframes btn-reveal-ifo-pulse {
    0%,
    100% {
        box-shadow:
            0 4px 14px -2px rgb(var(--t-accent) / 0.25),
            inset 0 0 0 1px rgb(var(--t-accent) / 0.2);
    }
    50% {
        box-shadow:
            0 6px 22px 0 rgb(var(--t-accent) / 0.45),
            inset 0 0 0 1px rgb(var(--t-accent) / 0.4);
    }
}

@keyframes btn-reveal-ifo-shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.ifo-cta-fade-enter-active,
.ifo-cta-fade-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}
.ifo-cta-fade-enter-from,
.ifo-cta-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, 1rem);
}

.section {
    @apply hidden;
}

.section-mobile-first {
    @apply hidden;
}

.show {
    @apply flex justify-center;
}

/* carousel */
.carousel {
    @apply mt-16;
    position: relative;
    overflow: hidden;
}

.carousel-inner {
    display: flex;
    transition: 0.5s ease-in-out;
}

.carousel-item {
    flex: 0 0 100%;
}

.carousel-image {
    width: 100%;
    height: auto;
    object-fit: cover;
}

.carousel-dots {
    position: absolute;
    bottom: 3rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
}

.dot-container {
    position: relative;
    margin: 0 10px;
}

.dot-white {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
}

.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #333;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
}

.dot.active {
    background-color: gray;
}

.carousel-btn {
    @apply flex flex-col items-center absolute z-30 bottom-32 w-full;
}

.btn-order {
    @apply bg-lime-eronor py-3 px-8 rounded-full absolute z-30 mx-auto border-2 border-white;
    /* bottom: 4rem; */
}

@media screen and (min-width: 400px) {
    .carousel {
        @apply mt-8;
    }
}

@media screen and (min-width: 400px) {
    .carousel {
        @apply mt-0;
    }
}

@media screen and (min-width: 800px) {
    .carousel {
        @apply hidden;
    }

    .section-mobile-first {
        @apply flex justify-center;
    }

    .catalog-category {
        @apply mx-4;
    }
}

@media screen and (min-width: 1020px) {
    .catalog-category {
        @apply mx-8;
    }

    .section-mobile-first {
        @apply flex justify-center;
    }
}

@media screen and (min-width: 1200px) {
    /* .section-mobile-first {
    @apply w-[70rem];
  } */
}

@media screen and (min-width: 1500px) {
    .catalog-category {
        @apply mx-8;
    }
}
</style>
